/**
 * The claim centre.
 *
 * On day one this is the whole product. A university that has just signed has
 * zero students on P3, and the single most valuable thing the person looking
 * at this screen can do in the next five minutes is send the link to students.
 * Every second of chrome between them and it is a second of decay, so the
 * link, the code and the QR are in the first paint, not behind a step and not
 * behind a menu.
 *
 * ── EVERY CONTROL HERE WORKS RIGHT NOW ───────────────────────────────────
 * Four copy actions and one download, all client side, no server round trip
 * and nothing stubbed. The one artifact that needs a server, the print-ready
 * poster PDF, carries a `Planned` chip and says so, because a button that
 * looks live and does nothing is the exact failure this rebuild exists to
 * correct.
 *
 * ── THE INVITATION (Sep 12 2026) ─────────────────────────────────────────
 * The second card is the answer to "we have eight hundred freshmen and a
 * six-character code". P3 cannot mail an institution's students itself: a
 * roster is an education record and P3's own addendum disclaims the status
 * that would let it hold one. So the institution sends it, from its own mail,
 * and P3 receives no addresses. The message is written here, finished, with
 * this institution's link and code already in it (lib/invite-message.ts).
 *
 * It is an ARTIFACT, not advice. The three "put it in front of students"
 * steps cut on Sep 9 2026 were advice; a message you copy and send is a tool.
 *
 * Its last paragraph is load-bearing: since Sep 17 2026 it says the
 * institution sees that you joined, by first name and last initial, and
 * whether you have a mentor, and never your messages, your email or what you
 * do in P3 as one person. Every clause is true (lib/roster.ts).
 *
 * ── BEFORE A CODE EXISTS (Sep 14 2026) ───────────────────────────────────
 * A card checkout opens the workspace on payment, but the join code is
 * minted when P3 connects the institution to the student app, by hand,
 * within one business day (Thomas: one click in Retool). Until then
 * `joinCode` is "" (live-view.ts). A link, a code, a QR and an invitation
 * built from nothing would each send a student to a join page that does not
 * know the school, so the centre shows one card saying when they arrive and
 * nothing a reader could copy.
 */

import { useEffect, useRef, useState } from "react";

import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  SrOnly,
} from "~/app/components/primitives";
/* Chip: the green "Active" mark on the join-link card ("Live now" in crimson
   until Sep 11 2026; Thomas: red read as an alert). The planned-poster chip and
   its three paragraphs of footnote left on Sep 9 2026: Thomas, "WAY too much
   explainer text, and different font styles/sizes on this section". */
import {
  type Institution,
} from "~/app/lib/demo-data";
import { inviteMessage, inviteMessageText } from "~/app/lib/invite-message";
import { QrCode } from "~/app/panels/qr";

/**
 * A copy button that reports what happened. The clipboard API throws in an
 * insecure context and in some embedded webviews, so the failure path selects
 * the text instead and says so, rather than silently doing nothing.
 */
function CopyButton({
  text,
  children,
  variant = "secondary",
  onCopied,
}: {
  text: string;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  onCopied?: (label: string) => void;
}) {
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState("done");
      onCopied?.(typeof children === "string" ? children : "Copied");
    } catch {
      setState("failed");
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 2400);
  }

  return (
    <Button variant={variant} size="sm" onClick={copy}>
      {state === "done" ? "Copied" : state === "failed" ? "Select and copy" : children}
      {state === "failed" ? (
        <SrOnly>
          Your browser blocked the clipboard. Select the text and copy it by hand.
        </SrOnly>
      ) : null}
    </Button>
  );
}

export function ClaimCentre({ institution }: { institution: Institution }) {
  const qrRef = useRef<SVGSVGElement>(null);
  const [status, setStatus] = useState("");

  if (institution.joinCode === "") {
    return (
      <div className="e-claimgrid">
        <Card>
          <CardHeader>
            <CardTitle>Your join link is on its way</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="e-body">
              P3 connects your institution to the student app within one
              business day of payment. Your link, six character code and QR
              code appear here, and we email you when they are ready.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  const invite = inviteMessage(institution);

  /** The QR downloads as the very SVG on screen, serialized. There is no
   *  second code path that could drift from the one the reader can see. */
  function downloadQr() {
    const node = qrRef.current;
    if (!node) return;
    const markup = new XMLSerializer().serializeToString(node);
    const blob = new Blob(
      [`<?xml version="1.0" encoding="UTF-8"?>\n${markup}`],
      { type: "image/svg+xml" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `p3-join-${institution.joinSlug}.svg`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setStatus("The QR image has been downloaded as an SVG.");
  }

  return (
    <div className="e-claimgrid">
      <Card>
        <CardHeader
          aside={<Chip tone="good">Active</Chip>}
        >
          <CardTitle sub="Anyone with this link or code can claim your institution in the app.">
            Your join link
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="e-vstack">
            <div className="e-copyrow">
              <code>{institution.joinUrl}</code>
              <CopyButton text={`https://${institution.joinUrl}`} variant="ghost">
                Copy link
              </CopyButton>
            </div>

            <div className="e-row" style={{ gap: "var(--sp-4)" }}>
              <div style={{ minWidth: 0 }}>
                <p className="e-label">Six-character code</p>
                <p className="e-joincode" style={{ marginTop: "var(--sp-2)" }}>
                  {institution.joinCode}
                </p>
                <div className="e-row" style={{ marginTop: "var(--sp-3)" }}>
                  <CopyButton text={institution.joinCode}>Copy code</CopyButton>
                  <Button variant="secondary" size="sm" onClick={downloadQr}>
                    Download the QR
                  </Button>
                </div>
              </div>

              <div className="e-qr">
                <QrCode
                  ref={qrRef}
                  text={`https://${institution.joinUrl}`}
                  label={`QR code linking to ${institution.joinUrl}, the join page for ${institution.legalName}`}
                />
              </div>
            </div>

            <p className="e-note" aria-live="polite">
              {status}
            </p>
          </div>
        </CardBody>
      </Card>

      {/* THE SENTENCE THAT USED TO SIT HERE WAS FALSE. It read "<domain> is
          verified, so a student who signs up with it can be offered <school>.
          They still choose." Nothing verified that domain: on a live tenant
          the string was the part of the signed-in admin's own email address
          after the @ (live-view.ts), `org_domains` exists only in the
          architecture document, and no student has ever been offered
          anything. It told every customer, in writing, about a capability
          that does not exist. Removed Sep 12 2026. When domain claiming is
          built, the sentence comes back with something behind it.

          "Put it in front of students" (three advice steps) was removed at
          Thomas's ask, Sep 9 2026: advice, not a finding. The card below is
          not that: it is the message itself, finished, to copy and send. */}

      <Card span="full">
        <CardHeader>
          <CardTitle sub="P3 never receives your students' addresses. You send this from your own mail, and it arrives from someone they know.">
            Invite your students
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="e-vstack">
            <div>
              <p className="e-label">Subject</p>
              <p className="e-ui" style={{ marginTop: "var(--sp-2)" }}>{invite.subject}</p>
            </div>
            <div>
              <p className="e-label">Message</p>
              <pre className="e-paste" style={{ marginTop: "var(--sp-2)" }}>{invite.body}</pre>
            </div>
            <div className="e-row">
              <CopyButton
                text={inviteMessageText(institution)}
                variant="primary"
                onCopied={() => setStatus("The invitation has been copied, subject and message.")}
              >
                Copy the invitation
              </CopyButton>
              <CopyButton
                text={invite.body}
                onCopied={() => setStatus("The message has been copied, without the subject line.")}
              >
                Copy the message only
              </CopyButton>
            </div>
          </div>
        </CardBody>
        <CardFootnote>
          <p className="e-note">
            Send it from a named person rather than a central address. It reads
            as an offer from an advisor, not a circular, and a student who
            recognises the sender opens it.
          </p>
        </CardFootnote>
      </Card>
    </div>
  );
}
